import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  Scissors, LogOut, Trash2, CreditCard, Banknote, 
  Check, BarChart3, ShoppingCart, Clock, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSPage = () => {
  const navigate = useNavigate();
  const { 
    currentUser, 
    currentBranch, 
    cart, 
    logout, 
    addToCart, 
    removeFromCart, 
    clearCart, 
    getCartTotal,
    createOrder 
  } = useApp();
  
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [successDialog, setSuccessDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState('');
  const [lastPaymentMethod, setLastPaymentMethod] = useState('');
  const [lastTipAmount, setLastTipAmount] = useState(0);
  const [tipAmount, setTipAmount] = useState('');
  const [customItemName, setCustomItemName] = useState('Vlastní cena');
  const [customItemPrice, setCustomItemPrice] = useState('');

  // Redirect if not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      if (!currentBranch) return;
      try {
        const response = await axios.get(`${API}/services/${currentBranch}`);
        setServices(response.data);
      } catch (err) {
        console.error('Failed to fetch services:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchServices();
  }, [currentBranch]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
      minimumFractionDigits: 0
    }).format(price);
  };

  const normalizeAmount = (value) => {
    const amount = Math.round(Number(value) || 0);
    return amount > 0 ? amount : 0;
  };

  const handleAddCustomItem = () => {
    const price = normalizeAmount(customItemPrice);
    if (!price) return;

    addToCart({
      id: `custom-${Date.now()}`,
      name: customItemName.trim() || 'Vlastní cena',
      duration: 'Vlastní částka',
      price,
      isCustom: true
    });

    setCustomItemPrice('');
    setCustomItemName('Vlastní cena');
  };

  const handlePayment = async (method) => {
    if (cart.length === 0) return;
    // open confirmation dialog before creating the order
    setPendingPaymentMethod(method);
    setConfirmDialog(true);
  };

  const handleConfirmPayment = async () => {
    if (cart.length === 0 || !pendingPaymentMethod) return;
    const normalizedTipAmount = normalizeAmount(tipAmount);
    const result = await createOrder(pendingPaymentMethod, normalizedTipAmount);
    if (result.success) {
      setLastPaymentMethod(pendingPaymentMethod);
      setLastTipAmount(normalizedTipAmount);
      setConfirmDialog(false);
      setSuccessDialog(true);
      setPendingPaymentMethod('');
      setTipAmount('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const branchName = currentBranch === 'smichov' ? 'Smíchov' : 'Stodůlky';
  const tipValue = normalizeAmount(tipAmount);
  const cartTotal = getCartTotal();
  const orderTotal = cartTotal + tipValue;

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-obsidian flex flex-col" data-testid="pos-page">
      {/* Header */}
      <header className="bg-paper border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scissors className="w-6 h-6 text-gold" strokeWidth={1.5} />
          <div>
            <h1 className="font-display text-xl font-bold gold-gradient-text">
              GOLD BARBERSHOP
            </h1>
            <p className="text-xs text-muted-foreground">{branchName}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-semibold">{currentUser.name}</p>
            <p className="text-xs text-muted-foreground capitalize">
              Holič
            </p>
          </div>
          <img
            src={currentUser.avatar}
            alt={currentUser.name}
            className="w-10 h-10 rounded-full border-2 border-gold"
          />
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/my-orders')}
            className="border-gold/50 text-gold hover:bg-gold hover:text-black"
            data-testid="my-orders-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Moje objednávky
          </Button>
          
          {currentUser.role === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="border-gold/50 text-gold hover:bg-gold hover:text-black"
              data-testid="admin-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistiky
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-destructive"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Receipt/Order Summary */}
        <div className="w-80 lg:w-96 flex flex-col border-r border-white/10 bg-paper">
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-gold" />
                Účtenka
              </h2>
              {cart.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearCart}
                  className="text-muted-foreground hover:text-destructive"
                  data-testid="clear-cart-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Smazat
                </Button>
              )}
            </div>
          </div>

          {/* Cart Items */}
          <ScrollArea className="flex-1 p-4">
            {cart.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Účtenka je prázdná</p>
                <p className="text-sm">Vyberte služby ze seznamu</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cart.map((item, index) => (
                  <div
                    key={item.cartId}
                    className="flex items-center justify-between p-3 bg-subtle rounded-lg group"
                    data-testid={`cart-item-${index}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.name}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {item.duration}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-gold">
                        {formatPrice(item.price)}
                      </span>
                      <button
                        onClick={() => removeFromCart(item.cartId)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/20 rounded transition-all"
                        data-testid={`remove-item-${index}`}
                      >
                        <X className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Total & Payment */}
          <div className="p-4 border-t border-white/10 bg-subtle/50">
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mezisoučet:</span>
                <span className="font-mono text-sm">{formatPrice(cartTotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Dýška:</span>
                <span className="font-mono text-sm text-gold">{formatPrice(tipValue)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-white/10 pt-2">
                <span className="text-lg font-semibold">Celkem:</span>
                <span className="text-2xl font-mono font-bold text-gold" data-testid="cart-total">
                  {formatPrice(orderTotal)}
                </span>
              </div>
            </div>

            <div className="mb-3">
              <label className="text-xs text-muted-foreground mb-1 block">Dýška</label>
              <input
                value={tipAmount}
                onChange={(e) => setTipAmount(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                className="w-full rounded-md bg-paper border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold font-mono"
                placeholder="0"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handlePayment('cash')}
                disabled={cart.length === 0}
                className="h-16 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg"
                data-testid="pay-cash-btn"
              >
                <Banknote className="w-6 h-6 mr-2" />
                Hotově
              </Button>
              <Button
                onClick={() => handlePayment('card')}
                disabled={cart.length === 0}
                className="h-16 bg-blue-600 hover:bg-blue-500 text-white font-bold text-lg"
                data-testid="pay-card-btn"
              >
                <CreditCard className="w-6 h-6 mr-2" />
                Kartou
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel - Services Grid */}
        <div className="flex-1 p-4 overflow-auto">
          <h2 className="font-semibold text-lg mb-4">Služby</h2>

          <div className="mb-4 grid gap-3 xl:grid-cols-[1.2fr_0.8fr_auto] bg-paper border border-white/10 rounded-lg p-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Název vlastní položky</label>
              <input
                value={customItemName}
                onChange={(e) => setCustomItemName(e.target.value)}
                className="w-full rounded-md bg-subtle border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold"
                placeholder="Vlastní cena"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Vlastní cena</label>
              <input
                value={customItemPrice}
                onChange={(e) => setCustomItemPrice(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                className="w-full rounded-md bg-subtle border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold font-mono"
                placeholder="0"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={handleAddCustomItem}
                disabled={!normalizeAmount(customItemPrice)}
                className="w-full bg-gold text-black hover:bg-gold-light font-semibold"
              >
                Přidat
              </Button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="h-32 bg-paper rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div
              className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            >
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => addToCart(service)}
                  className="bg-paper border border-white/5 hover:border-gold/50 rounded-lg p-4 flex flex-col items-center justify-center min-h-[130px] transition-all duration-200 hover:gold-glow-sm group"
                  data-testid={`service-${service.id}`}
                >
                  <p className="font-medium text-center mb-2 group-hover:text-gold transition-colors leading-tight">
                    {service.name}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                    <Clock className="w-3 h-3" />
                    {service.duration}
                  </p>
                  <p className="font-mono font-bold text-lg text-gold">
                    {formatPrice(service.price)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={successDialog} onOpenChange={setSuccessDialog}>
        <DialogContent className="bg-paper border border-gold/30 max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="sr-only">Platba dokončena</DialogTitle>
            <DialogDescription className="sr-only">
              Objednávka byla úspěšně zaplacena
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="w-20 h-20 mx-auto mb-4 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-emerald-500" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Platba dokončena!</h3>
            <p className="text-muted-foreground">
              {lastPaymentMethod === 'cash' ? 'Hotově' : 'Kartou'}
            </p>
            {lastTipAmount > 0 && (
              <p className="mt-2 text-sm text-gold font-semibold">
                Dýška: {formatPrice(lastTipAmount)}
              </p>
            )}
          </div>
          <Button
            onClick={() => setSuccessDialog(false)}
            className="w-full bg-gold text-black hover:bg-gold-light font-bold"
            data-testid="success-close-btn"
          >
            Další zákazník
          </Button>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent className="bg-paper border border-gold/30 max-w-md">
          <DialogHeader>
            <DialogTitle>Potvrzení objednávky</DialogTitle>
            <DialogDescription>Zkontrolujte položky a částku před přidáním objednávky do systému.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2 max-h-64 overflow-auto mb-4">
              {cart.map((item) => (
                <div key={item.cartId} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-muted-foreground">{item.duration}</p>
                  </div>
                  <div className="font-mono font-semibold text-gold">{formatPrice(item.price)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mezisoučet:</span>
                <span className="font-mono">{formatPrice(cartTotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Dýška:</span>
                <span className="font-mono text-gold">{formatPrice(tipValue)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold">Celkem k úhradě:</span>
                <span className="font-mono font-bold text-2xl text-gold">{formatPrice(orderTotal)}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>Upravit</Button>
            <Button onClick={handleConfirmPayment} className="bg-gold text-black">Potvrdit a zaplatit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSPage;
